import React from "react";
import {Avatar, Box} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import DesktopWindowsOutlinedIcon from "@mui/icons-material/DesktopWindowsOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import {RootState} from "../../../store";
import {makeLink} from "../../common/languages-helper";
import {EXTERNAL_LINKS} from "../../../config/external-links";
import SecondaryButton from "../../common/buttons/SecondaryButton";
import YellowButton from "../../common/buttons/YellowButton";
import {trackCtaClick} from "../../../analytics/events";

const emptyValue = "—";

// NOTE: profile stays editable in-app for now. The WordPress profile page
// (emdrlink.pl/konto-klienta/profile/) is a separate datastore that does not
// sync back to this panel (which reads users/profile from emdrlink-api), so
// redirecting there would leave edits invisible here. Revisit once WP<>API
// sync exists.

const PLAN_NAMES: Record<string, string> = {
    free: "Konto Darmowe",
    full: "Konto Full",
};

const getInitials = (name: string): string => {
    if (!name) return "?";
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "?";
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

// Wide top card: left = real user profile (name/role from store, avatar as
// initials placeholder since there is no profile image in the data model),
// right = therapist-search visibility, driven by the real (client-side)
// searchVisible toggle in the profile settings.
export const ProfileSearcherCard: React.FC = () => {
    const {t} = useTranslation();
    const profile = useSelector((state: RootState) => state.profile);
    // Search-listing status is WordPress-owned; App.tsx hydrates it into redux
    // once the token is ready, so we read that single, consistent value here.
    const visible = profile.searchVisible;

    return (
        <div className="dashboard-card card-lg">
            <div className="profile-combo">
                <div className="profile-side">
                    <div className="card-label">{t("User profile")}</div>
                    <div className="profile-identity">
                        <Avatar
                            src={profile.avatarUrl || undefined}
                            sx={{width: 96, height: 96, bgcolor: "#0e3c47", fontSize: 30}}
                        >
                            {getInitials(profile.name)}
                        </Avatar>
                        <div className="profile-meta">
                            <div className="profile-name">{profile.name || emptyValue}</div>
                            {profile.position
                                ? <div className="profile-role">{profile.position}</div>
                                : (profile.role && <div className="profile-role">{t(profile.role)}</div>)}
                            <span className="profile-line">
                                <DesktopWindowsOutlinedIcon sx={{fontSize: 16}} />
                                {t("Available online")}
                            </span>
                            <Box sx={{mt: 1}}>
                                <SecondaryButton href={makeLink("/profile")}>
                                    {t("Account settings")}
                                </SecondaryButton>
                            </Box>
                        </div>
                    </div>
                </div>

                <div className="searcher-side">
                    <div className="card-label">{t("Profile in therapist search")}</div>
                    <div className={"searcher-status" + (visible ? " visible" : "")}>
                        {visible ? t("Visible") : t("Not visible")}
                    </div>
                    <div className="searcher-help">
                        {visible
                            ? t("Your profile is visible in the therapist list. New patients can find you.")
                            : t("Complete your profile to appear in the therapist list. New patients will then be able to find you.")}
                    </div>
                    <Box sx={{display: "flex", justifyContent: "center"}}>
                        {visible
                            ? <SecondaryButton href={makeLink("/profile")}>{t("Edit profile")}</SecondaryButton>
                            : <YellowButton href={makeLink("/profile")}>{t("Complete profile")}</YellowButton>}
                    </Box>
                </div>
            </div>
        </div>
    );
};

// Subscription plan. Everyone is on the free tier for now, so we honestly show
// "Konto Darmowe" with an upgrade CTA (mocked link until billing exists).
export const PlanCard: React.FC = () => {
    const {t} = useTranslation();
    const profile = useSelector((state: RootState) => state.profile);
    const planName = PLAN_NAMES[profile.plan] || PLAN_NAMES.free;
    const isFree = profile.plan === "free";

    return (
        <div className="dashboard-card card-lg">
            <div className="card-label">{t("Your plan")}</div>
            <div className="plan-name">{planName}</div>
            {isFree && (
                <div className="card-placeholder" style={{marginBottom: 18}}>
                    {t("Upgrade to unlock group sessions and more.")}
                </div>
            )}
            <Box sx={{display: "flex"}}>
                <SecondaryButton href={makeLink("/profile")}>
                    {t("Plan settings")}
                </SecondaryButton>
            </Box>
        </div>
    );
};

// Quick stats. Only "active rooms" is backed by real data (number of the
// therapist's invites). Hours used is not tracked yet; group sessions reflect
// the plan (unavailable on the free tier).
export const StatsColumn: React.FC<StatsColumnProps> = ({activeRooms}) => {
    const {t} = useTranslation();
    const profile = useSelector((state: RootState) => state.profile);
    const groupAvailable = profile.plan !== "free";
    return (
        <div className="stats-col">
            <div className="stat-panel">
                <div className="stat-label">{t("Active rooms")}</div>
                <div className="stat-value">{activeRooms}</div>
            </div>
            <div className="stat-panel">
                <div className="stat-label">{t("Hours used")}</div>
                <div className="stat-value placeholder">{emptyValue}</div>
            </div>
            <div className={"stat-panel" + (groupAvailable ? " stat-accent" : "")}>
                <div className="stat-label">{t("Group sessions")}</div>
                {groupAvailable
                    ? <div className="stat-value">{t("Active")}</div>
                    : <div className="stat-value locked">
                        <LockOutlinedIcon sx={{fontSize: 18, mr: 0.5}} />
                        {t("Unavailable")}
                      </div>}
            </div>
        </div>
    );
};

interface StatsColumnProps {
    activeRooms: number;
}

interface PromoBannerProps {}

export const PromoBanner: React.FC<PromoBannerProps> = () => {
    const {t} = useTranslation();
    // Mocked destination — real advert-builder page to be wired up later.
    const ADVERT_URL = EXTERNAL_LINKS.advert;
    return (
        <div className="banner-inner" style={{display: "flex", alignItems: "center", gap: 16, flex: 1}}>
            <div className="banner-avatars">
                <Avatar sx={{bgcolor: "#8f7ce0"}}>A</Avatar>
                <Avatar sx={{bgcolor: "#b0a1ee"}}>B</Avatar>
                <Avatar sx={{bgcolor: "#6a4fd0"}}>C</Avatar>
            </div>
            <div
                className="banner-text"
                dangerouslySetInnerHTML={{
                    __html: t("<b>Create your advert</b> on our website and attract more patients!"),
                }}
            />
            <YellowButton onClick={() => { trackCtaClick("advertise"); window.open(ADVERT_URL, "_blank", "noopener,noreferrer"); }}>
                {t("Check it out")}
            </YellowButton>
        </div>
    );
};

export const DashboardFooter: React.FC = () => {
    const {t} = useTranslation();
    const year = new Date().getFullYear();
    return (
        <div className="dashboard-footer">
            <span>
                {t("Having trouble with the app?")}{" "}
                <a href="mailto:support@emdrlink.pl">{t("Send a technical report")}</a>
            </span>
            <span>© emdrlink 2021–{year}</span>
        </div>
    );
};

